/**
 * Ephemeral realtime overlay for non-past saved itineraries.
 *
 * Polls `POST /api/itinerary/realtime-enrich` and keeps an in-memory id → DTO map.
 * Never writes IndexedDB; scheduled snapshots stay the persistence source of truth.
 */

import { useEffect, useMemo, useState } from "react";
import { SEARCH_BAR } from "../../../config/global.configuration";
import { subscribeToPageVisibilityTransitions } from "../../../services/lifecycle/PageLifecycleStore";
import { ItineraryService } from "../../../services/ItineraryService";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";

/** Shared axios client for enrich polls (same pattern as list data-flow). */
const itineraryService = new ItineraryService();

/** Stable empty overlay returned when enrichment is inactive. */
const EMPTY_OVERLAY: Readonly<Record<string, ItineraryDTO>> = {};

/** Parameters for {@link useSavedItineraryRealtimeEnrich}. */
export interface UseSavedItineraryRealtimeEnrichParams {

    /**
     * Current + upcoming saved itineraries only (never past).
     * Empty / disabled skips network calls and clears the overlay.
     */
    "itineraries": ItineraryDTO[];

    /**
     * When `false`, aborts in-flight work and exposes an empty overlay.
     *
     * @defaultValue `true`
     */
    "enabled"?: boolean;
}

/** Result of {@link useSavedItineraryRealtimeEnrich}. */
export interface UseSavedItineraryRealtimeEnrichResult {

    /** Ephemeral live DTOs keyed by itinerary id (no IndexedDB write). */
    "overlayById": Readonly<Record<string, ItineraryDTO>>;
}

/**
 * Polls realtime enrichment for non-past saved itineraries and exposes an ephemeral overlay.
 *
 * @param params Current/upcoming itineraries and enable flag.
 * @returns In-memory overlay map keyed by itinerary id.
 *
 * @remarks
 * - Poll interval matches {@link SEARCH_BAR.refreshTimeIntervalMs} (~60s).
 * - Refreshes when the document becomes visible again (tab focus / app resume).
 * - Errors are swallowed so scheduled snapshots remain displayed; polling continues.
 */
export const useSavedItineraryRealtimeEnrich = ( params: UseSavedItineraryRealtimeEnrichParams ): UseSavedItineraryRealtimeEnrichResult => {
    const { itineraries, "enabled": enabledOption = true } = params;
    const [ overlayById, setOverlayById ] = useState<Record<string, ItineraryDTO>>( {} );
    const isEnrichActive = enabledOption && itineraries.length > 0;

    useEffect( () => {
        if ( !isEnrichActive ) {
            return;
        }

        const abortController = new AbortController();
        let isEffectActive = true;

        /**
         * Runs one enrich request for the itineraries captured by this effect.
         */
        const runEnrich = async(): Promise<void> => {
            try {
                const enrichedItineraries = await itineraryService.enrichSavedItinerariesRealtime(
                    itineraries,
                    abortController.signal,
                );
                if ( abortController.signal.aborted || !isEffectActive ) {
                    return;
                }

                const nextOverlay: Record<string, ItineraryDTO> = {};
                for ( const itinerary of enrichedItineraries ) {
                    if ( typeof itinerary.id === "string" && itinerary.id.length > 0 ) {
                        nextOverlay[itinerary.id] = itinerary;
                    }
                }
                setOverlayById( nextOverlay );
            } catch( error: unknown ) {
                if ( abortController.signal.aborted || !isEffectActive ) {
                    return;
                }

                // Keep scheduled snapshot displayed; the next poll or focus refresh retries.
                if ( error instanceof Error && error.name === "CanceledError" ) {
                    return;
                }
            }
        };

        void runEnrich();

        const pollIntervalId = window.setInterval( () => {
            void runEnrich();
        }, SEARCH_BAR.refreshTimeIntervalMs );

        const unsubscribeVisibility = subscribeToPageVisibilityTransitions( ( nextIsVisible ) => {
            if ( nextIsVisible ) {
                void runEnrich();
            }
        } );

        /**
         * Window focus covers some desktop cases where visibility alone is insufficient.
         */
        const handleWindowFocus = (): void => {
            void runEnrich();
        };
        window.addEventListener( "focus", handleWindowFocus );

        return () => {
            isEffectActive = false;
            abortController.abort();
            window.clearInterval( pollIntervalId );
            unsubscribeVisibility();
            window.removeEventListener( "focus", handleWindowFocus );
        };
    }, [
        isEnrichActive,
        itineraries,
    ] );

    const visibleOverlayById = useMemo( () => {
        if ( !isEnrichActive ) {
            return EMPTY_OVERLAY;
        }

        return overlayById;
    }, [
        isEnrichActive,
        overlayById,
    ] );

    return { "overlayById": visibleOverlayById };
};
